// ----------------------------------------------------------------------------
// Section: Bought Together
// ----------------------------------------------------------------------------
//
// Hosts Bought Together Bundle blocks (src/blocks/bought-together-bundle.liquid)
// with exactly one visible at a time — desktop tabs and a mobile disclosure
// (category select) both drive the same "show bundle" action.
//
// Each Bundle block resolves its own open/closed state independently in
// Liquid (a block can't reliably read a sibling's settings), so more than
// one panel can start visible until this reconciles it down to exactly one —
// preferring the block targeted by the URL fragment, if any. Same pattern as
// src/sections/ingredients/ingredients.js.
//
// Also owns both add-to-cart controls per Bundle block (individual product +
// add-all), posting straight through the shared cart ajax helper — this
// isn't a variant-picker form, just fixed variant ids from the block's own
// settings, so the heavier iamForm/iam-add-to-cart-button machinery (built
// for a single product's variant selection) doesn't apply here.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import { cart } from 'scripts/helpers/iamota-shopify-ajax';
import translate from 'scripts/helpers/iamota-translate';
import './_bought-together.scss';

// -------------------------------------
// Variables
// -------------------------------------

var SECTION_SELECTOR = '[data-section-type="bought-together"]';
var PANEL_SELECTOR = '[data-bundle-panel]';
var TAB_SELECTOR = '.js-bought-together-tab';
var TABLIST_SELECTOR = '[data-bought-together-tablist]';
var DISCLOSURE_TOGGLE_SELECTOR = '.js-bought-together-disclosure-toggle';
var DISCLOSURE_LIST_SELECTOR = '[data-bought-together-disclosure-list]';
var DISCLOSURE_OPTION_SELECTOR = '.js-bought-together-disclosure-option';
var ADD_SINGLE_SELECTOR = '[data-bundle-add-single]';
var ADD_ALL_SELECTOR = '[data-bundle-add-all]';
var TAB_TEMPLATE_SELECTOR = 'template[data-bought-together-tab-template]';
var DISCLOSURE_TEMPLATE_SELECTOR = 'template[data-bought-together-disclosure-template]';
var ITEM_ADDED_DURATION = 3000;

// -------------------------------------
// Category controls markup (block → section)
// -------------------------------------
// Each Bundle block renders its own tab trigger + disclosure option (see
// src/blocks/bought-together-bundle.liquid) because it's the only place
// that can reliably read its own Category label/icon — section.blocks[n]
// .settings.* doesn't resolve for a theme block from the section. Move
// that inert markup into the section's tablist/disclosure-list once, before
// anything queries for rendered tabs/options.
function distributeCategoryControls(section) {
  var tablist = section.querySelector(TABLIST_SELECTOR);
  var disclosureList = section.querySelector(DISCLOSURE_LIST_SELECTOR);
  section.querySelectorAll(TAB_TEMPLATE_SELECTOR).forEach(template => {
    if (tablist) {
      tablist.appendChild(template.content);
    }
    template.remove();
  });
  section.querySelectorAll(DISCLOSURE_TEMPLATE_SELECTOR).forEach(template => {
    if (disclosureList) {
      disclosureList.appendChild(template.content);
    }
    template.remove();
  });
}

// Liquid decides on category controls from section.blocks.size (it can't
// read a theme block's settings), but a Bundle block can still render
// nothing — e.g. a gift-only product (ON-374). Drop the controls when fewer
// than two panels actually rendered, and the whole section when none did.
function pruneCategoryControls(section) {
  var _section$querySelecto;
  var panels = section.querySelectorAll(PANEL_SELECTOR);
  if (panels.length === 0) {
    section.hidden = true;
    return;
  }
  if (panels.length > 1) {
    return;
  }
  (_section$querySelecto = section.querySelector('[data-bought-together-categories]')) === null || _section$querySelecto === void 0 || _section$querySelecto.remove();

  // The lone panel's tab is gone, so its tabpanel wiring would dangle.
  panels[0].removeAttribute('role');
  panels[0].removeAttribute('aria-labelledby');
}

// -------------------------------------
// Single-visible orchestration
// -------------------------------------

function resolveInitialTarget(panels) {
  if (panels.length === 0) {
    return null;
  }
  var hash = window.location.hash.slice(1);
  if (hash) {
    var hashTarget = panels.find(panel => panel.id === hash);
    if (hashTarget) {
      return hashTarget;
    }
  }
  var visible = panels.find(panel => !panel.hidden);
  return visible || panels[0];
}
function closeDisclosure(section) {
  var toggle = section.querySelector(DISCLOSURE_TOGGLE_SELECTOR);
  var list = section.querySelector(DISCLOSURE_LIST_SELECTOR);
  if (!toggle || !list) {
    return;
  }
  toggle.setAttribute('aria-expanded', 'false');
  list.hidden = true;
}
function openDisclosure(section) {
  var toggle = section.querySelector(DISCLOSURE_TOGGLE_SELECTOR);
  var list = section.querySelector(DISCLOSURE_LIST_SELECTOR);
  if (!toggle || !list) {
    return;
  }
  toggle.setAttribute('aria-expanded', 'true');
  list.hidden = false;
}

// The "show bundle" action (AC): the target Bundle block becomes visible,
// every other Bundle block is hidden, and on mobile the category disclosure
// closes.
function showBundle(section, targetId) {
  var panels = Array.from(section.querySelectorAll(PANEL_SELECTOR));
  var targetPanel = panels.find(panel => panel.id === targetId);
  if (!targetPanel) {
    return;
  }
  panels.forEach(panel => {
    var isTarget = panel === targetPanel;
    panel.hidden = !isTarget;
    panel.classList.toggle('is-active', isTarget);
  });
  section.querySelectorAll(TAB_SELECTOR).forEach(tab => {
    var isTarget = tab.dataset.bundleTarget === targetId;
    tab.classList.toggle('is-active', isTarget);
    tab.setAttribute('aria-selected', isTarget ? 'true' : 'false');
    tab.setAttribute('tabindex', isTarget ? '0' : '-1');
  });

  // The active option also gets pinned to the top of the (closed) list via
  // CSS order — see :has(.is-active) in _bought-together.scss — so it's
  // first when the disclosure is next opened, checkmark included.
  section.querySelectorAll(DISCLOSURE_OPTION_SELECTOR).forEach(option => {
    option.classList.toggle('is-active', option.dataset.bundleTarget === targetId);
  });
  var currentLabel = section.querySelector('[data-bundle-current-label-text]');
  if (currentLabel) {
    currentLabel.textContent = targetPanel.dataset.categoryLabel || '';
  }
  closeDisclosure(section);
}
function reconcileVisibility(section) {
  var panels = Array.from(section.querySelectorAll(PANEL_SELECTOR));
  var target = resolveInitialTarget(panels);
  if (target) {
    showBundle(section, target.id);
  }
}

// -------------------------------------
// Category controls (tabs + disclosure)
// -------------------------------------

function initTabs(section) {
  section.querySelectorAll(TAB_SELECTOR).forEach(tab => {
    tab.addEventListener('click', () => {
      showBundle(section, tab.dataset.bundleTarget);
    });
  });
  var tablist = section.querySelector(TABLIST_SELECTOR);
  if (!tablist) {
    return;
  }

  // Roving tabindex + arrow-key navigation (ARIA APG tabs pattern) — the
  // click handler above already re-shows the bundle when focus moves.
  tablist.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }
    var tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
    var currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1) {
      return;
    }
    var newIndex = currentIndex;
    if (event.key === 'ArrowRight') newIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') newIndex = 0;
    if (event.key === 'End') newIndex = tabs.length - 1;
    event.preventDefault();
    tabs[newIndex].focus();
    showBundle(section, tabs[newIndex].dataset.bundleTarget);
  });
}
function initDisclosure(section) {
  var toggle = section.querySelector(DISCLOSURE_TOGGLE_SELECTOR);
  if (toggle) {
    toggle.addEventListener('click', () => {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeDisclosure(section);
      } else {
        openDisclosure(section);
      }
    });
  }
  section.querySelectorAll(DISCLOSURE_OPTION_SELECTOR).forEach(option => {
    option.addEventListener('click', () => {
      showBundle(section, option.dataset.bundleTarget);
      toggle === null || toggle === void 0 || toggle.focus();
    });
  });
}

// Registered once at module scope (not per-section) — closes any open
// disclosure on an outside click or Escape, regardless of which section it
// belongs to.
function initGlobalDisclosureDismissal() {
  document.addEventListener('click', event => {
    document.querySelectorAll("".concat(DISCLOSURE_TOGGLE_SELECTOR, "[aria-expanded=\"true\"]")).forEach(toggle => {
      var wrapper = toggle.closest('.bought-together__disclosure');
      var section = toggle.closest(SECTION_SELECTOR);
      if (wrapper && section && !wrapper.contains(event.target)) {
        closeDisclosure(section);
      }
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') {
      return;
    }
    document.querySelectorAll("".concat(DISCLOSURE_TOGGLE_SELECTOR, "[aria-expanded=\"true\"]")).forEach(toggle => {
      var section = toggle.closest(SECTION_SELECTOR);
      if (section) {
        closeDisclosure(section);
        toggle.focus();
      }
    });
  });
}

// -------------------------------------
// Add to cart
// -------------------------------------

function setBusy(button, labelEl, busyText) {
  if (labelEl) {
    labelEl.dataset.originalLabel = labelEl.dataset.originalLabel || labelEl.textContent;
    labelEl.textContent = busyText;
  }
  button.disabled = true;
  button.classList.add('is-loading');
}
function setSettled(button, labelEl, finalText, restoreDelay) {
  if (labelEl && finalText) {
    labelEl.textContent = finalText;
  }
  window.setTimeout(() => {
    if (labelEl && labelEl.dataset.originalLabel) {
      labelEl.textContent = labelEl.dataset.originalLabel;
    }
    button.disabled = false;
    button.classList.remove('is-loading');
  }, restoreDelay);
}

// Individual product add (AC: "the first available variant of that product
// alone is added to the cart with a quantity of 1").
function initAddSingle(section) {
  section.querySelectorAll(ADD_SINGLE_SELECTOR).forEach(button => {
    button.addEventListener('click', () => {
      var variantId = button.dataset.variantId;
      if (!variantId || button.disabled) {
        return;
      }
      setBusy(button, button, translate('Adding to cart...', 'addToCart.adding_to_cart'));
      cart.addItem({
        id: variantId,
        quantity: 1
      }).then(() => {
        setSettled(button, button, translate('Item added to cart', 'addToCart.item_added'), ITEM_ADDED_DURATION);
      }).catch(() => {
        setSettled(button, button, null, 0);
      });
    });
  });
}

// Bundle add-all (AC: single multi-item cart request; same product twice →
// one line at quantity 2; failure adds nothing and shows an adjacent error).
function initAddAll(section) {
  section.querySelectorAll(ADD_ALL_SELECTOR).forEach(button => {
    var labelEl = button.querySelector('[data-bundle-add-all-label]');
    var promoContent = button.closest('.bought-together-bundle__promo-content');
    var errorEl = promoContent ? promoContent.querySelector('[data-bundle-add-all-error]') : null;
    button.addEventListener('click', () => {
      if (button.disabled) {
        return;
      }
      var firstVariantId = button.dataset.firstVariantId;
      var secondVariantId = button.dataset.secondVariantId;
      var sameProduct = button.dataset.sameProduct === 'true';
      if (!firstVariantId || !secondVariantId) {
        return;
      }
      var items = sameProduct ? [{
        id: firstVariantId,
        quantity: 2
      }] : [{
        id: firstVariantId,
        quantity: 1
      }, {
        id: secondVariantId,
        quantity: 1
      }];
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      setBusy(button, labelEl, translate('Adding to cart...', 'addToCart.adding_to_cart'));
      cart.addItems(items).then(() => {
        setSettled(button, labelEl, translate('Item added to cart', 'addToCart.item_added'), ITEM_ADDED_DURATION);
      }).catch(error => {
        setSettled(button, labelEl, null, 0);
        if (errorEl) {
          errorEl.textContent = (error === null || error === void 0 ? void 0 : error.message) || translate('Unable to add', 'addToCart.error');
          errorEl.hidden = false;
        }
      });
    });
  });
}

// -------------------------------------
// Init
// -------------------------------------

function initSection(section) {
  distributeCategoryControls(section);
  pruneCategoryControls(section);
  reconcileVisibility(section);
  initTabs(section);
  initDisclosure(section);
  initAddSingle(section);
  initAddAll(section);
}
function initBoughtTogether() {
  var container = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  initBoughtTogether();
  initGlobalDisclosureDismissal();
});
document.addEventListener('shopify:section:load', _ref => {
  var {
    target
  } = _ref;
  initBoughtTogether(target);
});
window.addEventListener('hashchange', function () {
  document.querySelectorAll(SECTION_SELECTOR).forEach(reconcileVisibility);
});